import { ClientIdService } from './../clientid-service/clientid.service';
import { AuthService, BroadcastService, DialogService, LocalizationService, LogService, MibpLogger } from 'root/services';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpEventType, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { from, NEVER, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap, switchMap} from 'rxjs/operators';
import { Router } from '@angular/router';
import { differenceInMinutes } from 'date-fns';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private auth = inject(AuthService);
  private http = inject(HttpClient);
  private clientIdService = inject(ClientIdService);
  private localizationService = inject(LocalizationService);
  private broadcastService = inject(BroadcastService);
  private router = inject(Router);


  private log: MibpLogger;

  constructor() {
    const log = inject(LogService);

    this.log = log.withPrefix('auth-interceptor');
  }

  /**
   * When this is true we have detected the "error 0 - Unknown Error" from backend calls,
   * and the release.json suggests we have a recent deploy.
   *
   * So then a message is shown and all requests to the backend will be blocked and user would have to reload page
   */
  private blockAllRequestsDuringMaintenance = false;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<HttpEventType.Response>> {
    return this.handle(req, next);
  }

  /**
   * Determine if we should include credentials (cookies) for this request.
   * 
   * This is only needed for session related calls where we want to set or remove cookies.
   */
  private shouldIncludeCredentials(url: string): boolean {
    url = url.toLowerCase();
    return url.endsWith('/session') 
      || url.endsWith('/session/refresh') 
      || url.endsWith('/session/signout') 
      || url.includes('/session/signout-everywhere');
  }

  private async resolveCustomHeaderValues(req: HttpRequest<any>): Promise<Record<string, string>> {
    const accessToken = await this.auth.ensureToken(req.url.endsWith('/session'));
    const headers = {};
    const clientId = this.clientIdService.getClientId();

    if (!req.url.includes('globalConfig')) {
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    if (clientId) {
      headers['X-Client-Id'] = clientId;
    }

    headers['x-lang-code'] = this.localizationService.getLang();

    return headers;
  }

  handle(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.blockAllRequestsDuringMaintenance || this.broadcastService.snapshot.sessionRevoked) {
      if (this.broadcastService.snapshot.sessionRevoked) {
        this.log.warn(`[Block] Session is revoked: ${req.url}`);
      } else {
        this.log.warn(`[Block] During maintenance: ${req.url}`);
      }
      return NEVER;
    }

    return from(this.resolveCustomHeaderValues(req))                      // First, get the custom headers we want to set
      .pipe(
        mergeMap(headers => this.setCustomHeaders(req, next, headers)),   // Secondly, set our custom headers on the request
        catchError(response => this.handleError(req, next, response))                // Third, some custom error handling
      );
  }

  private setCustomHeaders(req: HttpRequest<any>, next: HttpHandler, headers: Record<string, string>): Observable<HttpEvent<any>> {
    const authReq = req.clone({
      setHeaders: headers,
      withCredentials: this.shouldIncludeCredentials(req.url)
    });
    return next.handle(authReq);
  }

  private handleError(req: HttpRequest<any>, next: HttpHandler,response: HttpErrorResponse):  Observable<HttpEvent<any>> {

    // This is what we catch when we get CORS error when backend application is turned off (during deploy)
    if (response.status == 0 && response.statusText == 'Unknown Error') {
      this.log.info(`Deteted error that may occur during deploy`);
      return this.handleBackendErrorDuringDeploy(req, next, response);
    }

    // Redirect due to unauthorized error
    if (response.status == 403 && response.error?.errors[0]?.exceptionType == "MibpForbiddenException") {
      this.router.navigateByUrl('en/unauthorized', {skipLocationChange: true});
    }

    // Special handling of session revoked errors
    const errors = response.error?.errors;
    if (errors && response.status == 500 && errors[errors.length - 1]?.exceptionType == "MibpSessionRevokedException") {
      this.blockAllRequestsDuringMaintenance = true; // To stop all the following potential requests
      this.auth.signout(null, 'revoked');
      return NEVER;
    }
    return throwError(() => response);
  }
  

  private handleBackendErrorDuringDeploy(request: HttpRequest<any>, next: HttpHandler, originalError: HttpErrorResponse): Observable<HttpEvent<any>> {
    this.log.info(`Checking for a release.json file`);

    return this.tryGetReleaseFile().pipe(
      switchMap((releaseJsonResponse) => {
        if (releaseJsonResponse && Object.keys(releaseJsonResponse).length > 0) {

          // deployTime in this file is set in Release pipeline before code is deployed to azure
          if (releaseJsonResponse.deployTime) {
            const deployTime = new Date(releaseJsonResponse.deployTime);
            const minutesSinceDeploy = differenceInMinutes(new Date(), deployTime);
            this.log.info(`release.json found. Deploy was done ${minutesSinceDeploy} minutes ago. (${deployTime})`);
            if (differenceInMinutes(new Date(), deployTime) < 60) {
              this.log.info(`Showing maintenance message`);

              // If we get this error and we're close to a deploy, then we show the small maintenance message
              this.showMaintenanceMessage();
              return NEVER;
            }
          }
          return throwError(() => originalError);
        } else {
          return throwError(() => originalError);
        }
      }),
      catchError(err => {
        this.log.warn('Could not load release.json', err);
        return throwError(() => originalError);
      })
    );
  }

  private tryGetReleaseFile(): Observable<any> {
    return from(this.http.get(`/assets/release.json?c=${new Date().getTime()}`)
      .pipe(
        switchMap(releaseFileResponse => {
          return of(releaseFileResponse);
        }),
        catchError(() => {
          return of({});
        })
      ));
  }

  /**
   * Show a maintenance message covering the site.
   * This will only be shown if a deploy has been done recently and we get strange
   * errors from backend (error code = 0 and statusText == "Unknown Error")
   */
  private showMaintenanceMessage(): void {

    this.auth.clearCacheDueToError();

    if (document.querySelector('.error-maintenance-window')) {
      return;
    }

    this.blockAllRequestsDuringMaintenance = true;

    const div = document.createElement(`div`);
    div.classList.add('error-maintenance-window');
    div.classList.add('error-maintenance-window--hidden');
    div.innerHTML = `<div>
      <div class="icon"><span class="material-icon">handyman</span></div>
      <div><img src="/assets/images/logo_black.svg" alt="Sandvik Logotype"></div>
      <div><h1></h1></div>
      <p class="first">Sorry, we're down for scheduled maintenance right now.</p>
      <p class="second">Please wait a while and try again.</p>
    </div>`;
    div.querySelector('h1').textContent = this.localizationService.get('ScheduledMaintenanceError_Title', 'Scheduled maintenance');
    div.querySelector('p.first').textContent = this.localizationService.get('ScheduledMaintenanceError_Message1', `Sorry, we're down for scheduled maintenance right now.`);
    div.querySelector('p.second').textContent = this.localizationService.get('ScheduledMaintenanceError_Message2', `Please wait a while and try again`);

    document.body.append(div);
    document.querySelector('.error-maintenance-window').classList.add('error-maintenance-window--visible');

  }
}
